import React from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner } from '@/components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';

/** The tab -> the permission that opens it, in the default order. */
const SECTION_PERMISSIONS: Array<[string, string]> = [
  ['service', 'area:service_view'],
  ['foreman', 'area:foreman_view'],
  ['contract', 'area:contract_view'],
];

/**
 * Redirects `/areas` to the tab used last in this sign-in (V3-030 / FLT-09), or else to the
 * first areas section the user may view.
 */
const AreasDefaultRedirect: React.FC = () => {
  const { loading } = useAuth();
  const { hasPermission } = usePermissions();
  // Written by AreasPage (same page key and name).
  const [savedSection] = usePersistedListState<string>('areas', 'tab', '');

  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '200px' }}>
        <Spinner animation="border" role="status">
          <span className="tw-sr-only">Loading...</span>
        </Spinner>
      </div>
    );
  }

  // Only a tab the user may still view, so a stale entry can never bounce between the two routes.
  const saved = SECTION_PERMISSIONS.find(([section, perm]) => section === savedSection && hasPermission(perm));
  if (saved) {
    return <Navigate to={`/areas/${saved[0]}`} replace />;
  }
  const first = SECTION_PERMISSIONS.find(([, perm]) => hasPermission(perm));
  if (first) {
    return <Navigate to={`/areas/${first[0]}`} replace />;
  }

  return <Navigate to="/home" replace />;
};

export default AreasDefaultRedirect;
